import {message} from "antd";
import axios from "axios";
import {ethers, formatUnits, ZeroAddress} from 'ethers';
import { JsonRpcProvider, utils } from 'ethers'; // ✅ 正确导入 v6 的 JsonRpcProvider

// BSC RPC
const provider = new JsonRpcProvider('https://bsc-dataseed.binance.org/');

const factoryAddress = '0xBCfCcbde45cE874adCB698cC183deBcF17952812';
const factoryAbi = [
    'function getPair(address tokenA, address tokenB) external view returns (address pair)'
];

const pairAbi = [
    'function token0() external view returns (address)',
    'function token1() external view returns (address)'
];

const erc20Abi = [
    'function decimals() view returns (uint8)',
    'function balanceOf(address owner) view returns (uint256)'
];

const baseTokenAddress = '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c'; // WBNB

const fetchPairAddress = async (tokenAddress) => {
    const options = {
        method: 'GET',
        headers: {
            accept: 'application/json',
            'x-chain': 'bsc',
            'X-API-KEY': 'e5c520c4cd1b41bf8148309d55dad437'
        }
    };

    const url = `https://public-api.birdeye.so/defi/v2/markets?address=${tokenAddress}&time_frame=24h&sort_type=desc&sort_by=liquidity&offset=0&limit=10`;

    try {
        const res = await fetch(url, options);
        const json = await res.json();
        const pairAddress = json?.data?.items?.[0]?.source;
        if (!pairAddress) throw new Error("Pair address not found in API response");
        return pairAddress;
    } catch (err) {
        console.error('Failed to fetch pair address:', err);
        throw err;
    }
};


async function getPoolInfo(tokenAddress) {
    const pairAddress = await fetchPairAddress(tokenAddress);
    console.log("Pair address is:", pairAddress);

    const pair = new ethers.Contract(pairAddress, pairAbi, provider);
    const tokenContract = new ethers.Contract(tokenAddress, erc20Abi, provider);
    const baseContract = new ethers.Contract(baseTokenAddress, erc20Abi, provider);

    const [tokenDecimals, baseDecimals] = await Promise.all([
        tokenContract.decimals(),
        baseContract.decimals(),
    ]);

    const [tokenBalanceRaw, baseBalanceRaw] = await Promise.all([
        tokenContract.balanceOf(pairAddress),
        baseContract.balanceOf(pairAddress),
    ]);

    const tokenReserve = Number(formatUnits(tokenBalanceRaw, tokenDecimals));
    const baseReserve = Number(formatUnits(baseBalanceRaw, baseDecimals));

    return {
        baseReserve,
        tokenReserve
    };
}

// 根据目标价格估算需要买入（正值）或卖出（负值）多少 WBNB
// 根据目标价格，估算需要买入的 WBNB 或卖出的 Token 数量
export const estimateAmountByPrice = async (tokenAddress, targetPrice) => {
    const pool = await getPoolInfo(tokenAddress);
    const { baseReserve, tokenReserve } = pool;

    const currentPrice = baseReserve / tokenReserve;
    const k = baseReserve * tokenReserve;

    if (targetPrice === currentPrice) return 0;

    // 买入逻辑：目标价格更高
    if (targetPrice > currentPrice) {
        const newBaseReserve = Math.sqrt(k * targetPrice);
        const deltaBase = newBaseReserve - baseReserve; // WBNB
        return deltaBase;
    }

    // 卖出逻辑：目标价格更低
    // 推导：newTokenReserve = sqrt(k / targetPrice)
    const newTokenReserve = Math.sqrt(k / targetPrice);
    const deltaToken = tokenReserve - newTokenReserve; // 卖出的 token 数量
    console.log("卖出:", deltaToken);
    return deltaToken; // 用负号表示卖出
};


// 根据金额（正为买入，负为卖出）估算交易后的价格（池子spot价格）
// 根据输入金额（正为买入 WBNB，负为卖出 Token）估算交易后的价格
export const estimatePriceByAmount = async (tokenAddress, amount) => {
    const pool = await getPoolInfo(tokenAddress);
    const { baseReserve, tokenReserve } = pool;

    const k = baseReserve * tokenReserve;

    let newBaseReserve, newTokenReserve;

    if (amount >= 0) {
        // 买入 WBNB
        newBaseReserve = baseReserve + amount;
        newTokenReserve = k / newBaseReserve;
    } else {
        // 卖出 Token
        const tokenDelta = -amount;
        newTokenReserve = tokenReserve + tokenDelta;
        newBaseReserve = k / newTokenReserve;
    }

    if (newBaseReserve <= 0 || newTokenReserve <= 0) {
        throw new Error("Invalid reserve values after trade");
    }

    const newPrice = newBaseReserve / newTokenReserve;
    return newPrice;
};



export const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp * 1000); // 将秒级时间戳转换为毫秒级
    return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false, // 24小时制
    }).replace(/\//g, '-'); // 将日期中的 / 替换为 -
};

export const formattedAddress =(text) => {
    return text && text.length > 10
        ? `${text.slice(0, 5)}...${text.slice(-5)}`
        : text; // 地址过短时，不做省略
}

export const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
        message.success('地址已复制到剪贴板');
    }).catch(() => {
        message.error('复制失败');
    });
};


const API_URL = '/api/v1/chain/list';

export const fetchTokenOptions = async () => {
    try {
        const response = await axios.get(API_URL);
        if (response.data.code !== 0) {
            console.error('获取链数据失败:', response.data.msg);
            return {};
        }

        const chainsData = response.data.data;
        const formattedData = {};

        // 解析返回的数据
        Object.values(chainsData).forEach(chain => {
            const { chain_name, common_address_map } = chain;

            if (chain_name && common_address_map) {
                formattedData[chain_name] = Object.entries(common_address_map).map(([name, address]) => ({
                    name,
                    address,
                }));
            }
        });

        return formattedData;
    } catch (error) {
        console.error('获取 tokenOptions 失败:', error);
        return {};
    }
};

const chainScanMap = {
    56: 'https://bscscan.com',
    97: 'https://testnet.bscscan.com',
    8453: 'https://basescan.org',
    84532: 'https://base-sepolia.blockscout.com',
    // 其他链按需添加
};

export const getScanUrl = (chain_id, type = 'address', value = '') => {
    const base = chainScanMap[chain_id] || 'https://etherscan.io';
    return `${base}/${type}/${value}`;
};